/**
 * CTAConfigSection
 *
 * Configures Call-to-Action buttons and destination URLs during bulk deployment.
 * Supports:
 * - CTA type selection (LEARN_MORE, SHOP_NOW, etc.)
 * - URL destinations with UTM support
 * - Lead form selection (Meta only)
 * - Product page linking (from Strategy Hub)
 * - Stage-based defaults
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
    Link,
    ExternalLink,
    FileText,
    ShoppingBag,
    ChevronDown,
    ChevronUp,
    Check,
    Plus,
    Package
} from 'lucide-react';
import { CTA_OPTIONS } from '../funnel-builder/forms/AdForm';

// Stage-based CTA - no defaults, user must select
const STAGE_CTA_DEFAULTS = {
    TOFU: { cta: null, destinationType: 'url' },
    MOFU: { cta: null, destinationType: 'url' },
    BOFU: { cta: null, destinationType: 'product' }
};

// Destination type options
const DESTINATION_TYPES = [
    { value: 'url', label: 'Landing Page URL', icon: Link, description: 'Direct link to any webpage' },
    { value: 'lead_form', label: 'Lead Form', icon: FileText, description: 'Meta lead generation form', platforms: ['meta'] },
    { value: 'product', label: 'Product Page', icon: Package, description: 'Link to product from Strategy Hub' },
    { value: 'app', label: 'App Install', icon: ExternalLink, description: 'App store listing', platforms: ['meta', 'google', 'tiktok'] }
];

const CTAConfigSection = ({
    funnelStage = 'TOFU',
    ctaConfig = {},
    onConfigChange,
    linkedProduct = null,  // Product linked from Strategy Hub
    availableLeadForms = [],  // Lead forms from Meta
    platform = 'meta',
    brandDefaultUrl = null,  // Brand's default landing page
    utmEnabled = false
}) => {
    const [isExpanded, setIsExpanded] = useState(true);

    // Get stage-based defaults
    const defaults = STAGE_CTA_DEFAULTS[funnelStage] || STAGE_CTA_DEFAULTS.TOFU;

    // Determine smart default destination
    const smartDefaultDestination = useMemo(() => {
        // Priority 1: Linked product from Strategy Hub
        if (linkedProduct?.url) {
            return {
                type: 'product',
                url: linkedProduct.url,
                productId: linkedProduct.id,
                source: 'strategy_hub'
            };
        }
        // Priority 2: Brand's default landing page
        if (brandDefaultUrl) {
            return {
                type: 'url',
                url: brandDefaultUrl,
                source: 'brand_default'
            };
        }
        // Priority 3: Manual entry required
        return { type: 'url', url: '', source: 'manual' };
    }, [linkedProduct, brandDefaultUrl]);

    // Initialize config without default CTA - user must select
    useEffect(() => {
        if (ctaConfig.cta === undefined) {
            const initialConfig = {
                cta: null, // No default - user must select
                destinationType: smartDefaultDestination.type,
                url: smartDefaultDestination.url || '',
                productId: smartDefaultDestination.productId || null,
                leadFormId: null,
                appStoreUrl: null
            };
            onConfigChange?.(initialConfig);
        }
    }, []);  // Only run once on mount

    // Current values - no CTA fallbacks, user must select
    const currentCta = ctaConfig.cta || null;
    const currentDestinationType = ctaConfig.destinationType || smartDefaultDestination.type;
    const currentUrl = ctaConfig.url || smartDefaultDestination.url || '';

    // Filter destination types by platform
    const availableDestinationTypes = useMemo(() => {
        return DESTINATION_TYPES.filter(dt => {
            if (!dt.platforms) return true;
            return dt.platforms.includes(platform);
        });
    }, [platform]);

    // Handle CTA change
    const handleCtaChange = (value) => {
        onConfigChange?.({
            ...ctaConfig,
            cta: value
        });
    };

    // Handle destination type change
    const handleDestinationTypeChange = (type) => {
        const updates = {
            ...ctaConfig,
            destinationType: type
        };

        // Set appropriate URL based on type
        if (type === 'product' && linkedProduct?.url) {
            updates.url = linkedProduct.url;
            updates.productId = linkedProduct.id;
        } else if (type === 'url' && brandDefaultUrl) {
            updates.url = brandDefaultUrl;
        }

        onConfigChange?.(updates);
    };

    // Handle URL change
    const handleUrlChange = (value) => {
        onConfigChange?.({
            ...ctaConfig,
            url: value
        });
    };

    // Handle lead form change
    const handleLeadFormChange = (formId) => {
        onConfigChange?.({
            ...ctaConfig,
            leadFormId: formId
        });
    };

    return (
        <div style={{
            padding: 'var(--space-4)',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)'
        }}>
            {/* Header */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <ExternalLink size={20} style={{ color: 'var(--color-primary)' }} />
                    <div>
                        <h4 style={{ margin: 0 }}>Call to Action</h4>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                            {CTA_OPTIONS.find(c => c.value === currentCta)?.label || currentCta}
                            {currentUrl && ` → ${currentUrl.substring(0, 30)}...`}
                        </span>
                    </div>
                </div>
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div style={{ marginTop: 'var(--space-4)' }}>
                    {/* CTA Type Selection */}
                    <div style={{ marginBottom: 'var(--space-4)' }}>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-2)'
                        }}>
                            Button Type
                        </label>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                            gap: 'var(--space-2)'
                        }}>
                            {CTA_OPTIONS.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => handleCtaChange(option.value)}
                                    style={{
                                        padding: 'var(--space-2)',
                                        background: currentCta === option.value
                                            ? 'var(--color-primary)'
                                            : 'var(--color-bg-primary)',
                                        color: currentCta === option.value
                                            ? 'white'
                                            : 'var(--color-text-primary)',
                                        border: currentCta === option.value
                                            ? 'none'
                                            : '1px solid var(--color-border)',
                                        borderRadius: 'var(--radius-md)',
                                        cursor: 'pointer',
                                        fontSize: 'var(--font-size-sm)',
                                        textAlign: 'center',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    {option.label}
                                    {option.value === defaults.cta && currentCta !== option.value && (
                                        <span style={{
                                            display: 'block',
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-tertiary)'
                                        }}>
                                            Suggested
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Destination Type Selection */}
                    <div style={{ marginBottom: 'var(--space-4)' }}>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-2)'
                        }}>
                            Destination Type
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                            {availableDestinationTypes.map(destType => {
                                const Icon = destType.icon;
                                const isSelected = currentDestinationType === destType.value;
                                const isDisabled = destType.value === 'lead_form' && availableLeadForms.length === 0;
                                const showLinkedProduct = destType.value === 'product' && linkedProduct;

                                return (
                                    <div
                                        key={destType.value}
                                        onClick={() => !isDisabled && handleDestinationTypeChange(destType.value)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-3)',
                                            padding: 'var(--space-3)',
                                            background: isSelected
                                                ? 'var(--color-primary-alpha)'
                                                : 'var(--color-bg-primary)',
                                            borderRadius: 'var(--radius-md)',
                                            border: isSelected
                                                ? '2px solid var(--color-primary)'
                                                : '1px solid var(--color-border)',
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            opacity: isDisabled ? 0.5 : 1,
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <div style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            border: isSelected ? 'none' : '2px solid var(--color-border)',
                                            background: isSelected ? 'var(--color-primary)' : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            {isSelected && <Check size={12} style={{ color: 'white' }} />}
                                        </div>
                                        <Icon size={18} style={{ color: 'var(--color-text-secondary)' }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 500 }}>{destType.label}</div>
                                            <div style={{
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--color-text-secondary)'
                                            }}>
                                                {destType.description}
                                            </div>
                                            {showLinkedProduct && (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-1)',
                                                    marginTop: 'var(--space-1)',
                                                    fontSize: 'var(--font-size-sm)',
                                                    color: 'var(--success-600)'
                                                }}>
                                                    <Package size={12} />
                                                    {linkedProduct.name}
                                                </div>
                                            )}
                                        </div>
                                        {isDisabled && destType.value === 'lead_form' && (
                                            <span style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-tertiary)'
                                            }}>
                                                No forms available
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Destination-specific inputs */}
                    {currentDestinationType === 'url' && (
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-2)'
                            }}>
                                Landing Page URL
                            </label>
                            <input
                                type="url"
                                value={currentUrl}
                                onChange={(e) => handleUrlChange(e.target.value)}
                                placeholder="https://example.com/landing-page"
                                className="input-field"
                                style={{ width: '100%' }}
                            />
                            {utmEnabled && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    marginTop: 'var(--space-2)',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    <Check size={14} style={{ color: 'var(--success-500)' }} />
                                    UTM parameters will be auto-appended
                                </div>
                            )}
                        </div>
                    )}

                    {currentDestinationType === 'product' && (
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            {linkedProduct ? (
                                <div style={{
                                    padding: 'var(--space-3)',
                                    background: 'var(--success-50)',
                                    borderRadius: 'var(--radius-md)',
                                    border: '1px solid var(--success-200)'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        {linkedProduct.imageUrl && (
                                            <img
                                                src={linkedProduct.imageUrl}
                                                alt={linkedProduct.name}
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 'var(--radius-sm)',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        )}
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{linkedProduct.name}</div>
                                            <div style={{
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--color-text-secondary)'
                                            }}>
                                                From Strategy Hub
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--success-700)',
                                        wordBreak: 'break-all'
                                    }}>
                                        {linkedProduct.url}
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    padding: 'var(--space-3)',
                                    background: 'var(--color-bg-tertiary)',
                                    borderRadius: 'var(--radius-md)',
                                    textAlign: 'center'
                                }}>
                                    <ShoppingBag size={24} style={{
                                        color: 'var(--color-text-tertiary)',
                                        marginBottom: 'var(--space-2)'
                                    }} />
                                    <div style={{
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        No product linked. Use URL destination instead or link a product in Strategy Hub.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {currentDestinationType === 'lead_form' && (
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-2)'
                            }}>
                                Select Lead Form
                            </label>
                            {availableLeadForms.length > 0 ? (
                                <select
                                    className="select-field"
                                    value={ctaConfig.leadFormId || ''}
                                    onChange={(e) => handleLeadFormChange(e.target.value)}
                                    style={{ width: '100%' }}
                                >
                                    <option value="">Select a form...</option>
                                    {availableLeadForms.map(form => (
                                        <option key={form.id} value={form.id}>
                                            {form.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div style={{
                                    padding: 'var(--space-3)',
                                    background: 'var(--color-bg-tertiary)',
                                    borderRadius: 'var(--radius-md)',
                                    textAlign: 'center',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    No lead forms available. Create forms in Meta Business Suite.
                                </div>
                            )}
                        </div>
                    )}

                    {currentDestinationType === 'app' && (
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-2)'
                            }}>
                                App Store URL
                            </label>
                            <input
                                type="url"
                                value={ctaConfig.appStoreUrl || ''}
                                onChange={(e) => onConfigChange?.({ ...ctaConfig, appStoreUrl: e.target.value })}
                                placeholder="https://apps.apple.com/... or https://play.google.com/..."
                                className="input-field"
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {/* Stage-based recommendation */}
                    <div style={{
                        padding: 'var(--space-3)',
                        background: 'var(--info-50)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--info-200)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        <div style={{ fontWeight: 500, color: 'var(--info-700)', marginBottom: 'var(--space-1)' }}>
                            {funnelStage} Stage Recommendation
                        </div>
                        <div style={{ color: 'var(--info-600)' }}>
                            {funnelStage === 'TOFU' && 'Use educational content links with "Learn More" to build awareness'}
                            {funnelStage === 'MOFU' && 'Lead forms or "Sign Up" CTAs work well for consideration stage'}
                            {funnelStage === 'BOFU' && 'Direct product links with "Shop Now" maximize conversions'}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CTAConfigSection;

// Export constants for use elsewhere
export { STAGE_CTA_DEFAULTS, DESTINATION_TYPES };
